import React from 'react';
import { observer } from 'mobx-react';
import { Table, Select, Spin } from 'antd';
import {
    useApplicationSelectionStore,
    useAuthStore,
    useFeaturedStore,
} from '../../contexts/mobx';
import { useSearchParams } from '../../utils/use-search-params';
import { SearchParams, Featured } from '../../stores/featured';
import { ColumnProps } from 'antd/es/table';
import { CreateButton } from '../../components/action-buttons';
import { ImageHolder } from '../../components/image-holder';
import { PageTopBar } from '../../components/page-top-bar';
import { PaginationBar } from '../../components/pagination/pagination-bar';
import { Tag } from '../../components/tag';
import { useNavigation } from '../../utils/use-navigation';
import { FeatureTypeTagVariants, ActiveQueryParam } from '../../utils/const';
import { Text } from '../../utils/text';

const renderIsActive = (_: unknown, record: Featured) => {
    return record.isActive ? (
        <Tag variant="success">Active</Tag>
    ) : (
        <Tag variant="error">Inactive</Tag>
    );
};

const renderImage = (_: unknown, record: Featured) => {
    return (
        <ImageHolder
            src={record.items[0].backgroundImage ?? undefined}
            size={70}
        />
    );
};

const renderType = (_: unknown, record: Featured) => {
    return (
        <>
            <div>
                <Tag variant={FeatureTypeTagVariants[record.type]}>
                    {record.type}
                </Tag>
                {record.type === 'external_url' && (
                    <div className="light_label lil_label">
                        {record.items[0].externalUrl}
                    </div>
                )}
                {record.geolocated && (
                    <Tag variant="error" style={{ marginLeft: 10 }}>
                        {Text.featuredPage.tags.geolocated}
                    </Tag>
                )}
            </div>
        </>
    );
};

export const FeaturedPage = observer(() => {
    const featuredStore = useFeaturedStore();
    const applicationSelectionStore = useApplicationSelectionStore();
    const navigation = useNavigation();
    const authStore = useAuthStore();

    const { hasManagementAccess } = authStore;
    const appTarget = applicationSelectionStore.appTarget;

    if (!hasManagementAccess) {
        navigation.goToHome();
    }

    React.useEffect(() => {
        if (!appTarget) {
            navigation.goToHome();
        }
    }, [appTarget, navigation]);

    const columns: ColumnProps<Featured>[] = [
        {
            title: Text.featuredPage.columnTitle.active,
            key: 'is_active',
            render: renderIsActive,
        },
        {
            title: Text.featuredPage.columnTitle.image,
            key: 'image',
            render: renderImage,
        },
        {
            title: Text.featuredPage.columnTitle.type,
            render: renderType,
        },
    ];

    const search = useSearchParams<SearchParams>();

    React.useEffect(() => {
        if (appTarget) {
            featuredStore.getPaginatedList({ ...search.params, appTarget });
        }
    }, [featuredStore, search.params, appTarget]);

    return (
        <>
            <PageTopBar title={Text.featuredPage.pageTitle}>
                <CreateButton
                    onClick={() => navigation.replaceToFeaturedCreation()}
                />
            </PageTopBar>

            <div className={`action_zone_above_table`}>
                <div className="table_header_options_bar">
                    <Select<ActiveQueryParam>
                        placeholder="Status"
                        style={{ width: 120 }}
                        value={search.params.filter}
                        onChange={(a) =>
                            search.setParams({ filter: a, page: 0 })
                        }
                    >
                        {ActiveQueryParam.map((a) => (
                            <Select.Option value={a} key={a}>
                                {a}
                            </Select.Option>
                        ))}
                    </Select>
                </div>
                <PaginationBar
                    pagination={{
                        page: search.params.page,
                        pageSize: search.params.perPage,
                        total: featuredStore.paginatedList?.total,
                        onPageChange: (page) => search.setParams({ page }),
                        onPageSizeChange: (perPage) =>
                            search.setParams({ perPage, page: 0 }),
                    }}
                />
            </div>

            {featuredStore.isFetchingList ? (
                <Spin />
            ) : (
                <Table<Featured>
                    className="clickable_table_body_rows"
                    rowKey="id"
                    dataSource={featuredStore.paginatedList?.results}
                    columns={columns}
                    loading={featuredStore.isFetchingList}
                    pagination={false}
                    bordered
                    onRow={(r) => ({
                        onClick: () => navigation.goToFeaturedEdit(r.id),
                    })}
                />
            )}
        </>
    );
});
